/**
 * Complete Banxa-supported cryptocurrencies and blockchains data.
 * TypeScript conversion of the crypto data module.
 * Icons are stored locally in /crypto-icons/{ticker}.svg
 */

// ── TypeScript interfaces ──

export interface BlockchainInfo {
  name: string;
  shortName?: string;
  icon: string;
}

export interface CryptoCurrency {
  code: string;
  name: string;
  chains: string[];
  popular: boolean;
}

export interface FiatCurrency {
  code: string;
  name: string;
}

export interface Skin {
  id: string;
  name: string;
  accent: string | null;
}

export interface CryptoChainCombo {
  code: string;
  name: string;
  chain: string;
  /** Display name from provider (e.g. "Ethereum Mainnet") */
  chainName?: string;
  popular: boolean;
}

// ── Blockchain / Network definitions ──
// Maps blockchain codes to display names and their icon ticker

export const BLOCKCHAINS: Record<string, BlockchainInfo> = {
  // Canonical IDs (returned by the API — lowercase full names)
  // These cover cases where the canonical ID differs from the legacy uppercase key
  ethereum: { name: 'Ethereum (ERC20)', shortName: 'Ethereum', icon: 'eth' },
  bitcoin: { name: 'Bitcoin', icon: 'btc' },
  solana: { name: 'Solana', icon: 'sol' },
  polygon: { name: 'Polygon', icon: 'pol' },
  arbitrum: { name: 'Arbitrum', icon: 'arb' },
  avalanche: { name: 'Avalanche C-Chain', shortName: 'Avalanche', icon: 'avax' },
  litecoin: { name: 'Litecoin', icon: 'ltc' },
  tezos: { name: 'Tezos', icon: 'xtz' },
  sei: { name: 'SEI', icon: 'sei' },
  imx: { name: 'Immutable X', shortName: 'Immutable', icon: 'imx' },
  galachain: { name: 'Gala', icon: 'gala' },
  zksync2: { name: 'zkSync Era', shortName: 'zkSync', icon: 'zksync' },
  zks: { name: 'zkSpace', icon: 'zks' },
  'flow-evm': { name: 'Flow (EVM)', shortName: 'Flow', icon: 'flow' },
  vara: { name: 'Vara', icon: 'vara' },
  venom: { name: 'Venom', icon: 'venom' },
  hsk: { name: 'HashKey Chain', shortName: 'HashKey', icon: 'hsk' },
  mck: { name: 'MCK', icon: 'mck' },
  peer: { name: 'Peer', icon: 'peer' },
  // Testnets (fallback display names)
  goerli: { name: 'Goerli Testnet', shortName: 'Goerli', icon: 'eth' },
  hbartestnet: { name: 'Hedera Testnet', shortName: 'Hedera', icon: 'hbar' },
  arbitrumrinkeby: { name: 'Arbitrum Testnet', shortName: 'Arbitrum', icon: 'arb' },
  dydxtestnet: { name: 'dYdX Testnet', shortName: 'dYdX', icon: 'stark' },
  'zksync-rinkeby': { name: 'zkSync Testnet', shortName: 'zkSync', icon: 'zksync' },

  // Legacy uppercase keys (used by local crypto data and older integrations)
  ETH: { name: 'Ethereum (ERC20)', shortName: 'Ethereum', icon: 'eth' },
  BSC: { name: 'Binance Smart Chain (BEP20)', shortName: 'Binance', icon: 'bnb' },
  BTC: { name: 'Bitcoin', icon: 'btc' },
  SOL: { name: 'Solana', icon: 'sol' },
  MATIC: { name: 'Polygon', icon: 'pol' },
  ARB: { name: 'Arbitrum', icon: 'arb' },
  BASE: { name: 'Base', icon: 'base' },
  OPTIMISM: { name: 'Optimism', icon: 'op' },
  AVAX_C: { name: 'Avalanche C-Chain', shortName: 'Avalanche C', icon: 'avax' },
  AVAX_X: { name: 'Avalanche X-Chain', shortName: 'Avalanche X', icon: 'avax' },
  ADA: { name: 'Cardano', icon: 'ada' },
  DOT: { name: 'Polkadot', icon: 'dot' },
  TRON: { name: 'Tron (TRC20)', shortName: 'Tron', icon: 'trx' },
  XRP: { name: 'Ripple', icon: 'xrp' },
  XLM: { name: 'Stellar', icon: 'xlm' },
  ALGO: { name: 'Algorand', icon: 'algo' },
  ATOM: { name: 'Cosmos', icon: 'atom' },
  NEAR: { name: 'NEAR Protocol', shortName: 'NEAR', icon: 'near' },
  SUI: { name: 'Sui', icon: 'sui' },
  APT: { name: 'Aptos', icon: 'apt' },
  TON: { name: 'Ton', icon: 'ton' },
  FIL: { name: 'Filecoin', icon: 'fil' },
  FLOW: { name: 'Flow', icon: 'flow' },
  HBAR: { name: 'Hedera Hashgraph', shortName: 'Hedera', icon: 'hbar' },
  INJ: { name: 'Injective', icon: 'inj' },
  SEI_EVM: { name: 'SEI (EVM)', shortName: 'SEI', icon: 'sei' },
  ZKSYNC: { name: 'zkSync', icon: 'zksync' },
  LINEA: { name: 'Linea', icon: 'linea' },
  STARK: { name: 'StarkNet', icon: 'strk' },
  LN: { name: 'Lightning', icon: 'btc' },
  CELO: { name: 'Celo', icon: 'celo' },
  CRO: { name: 'Cronos', icon: 'cro' },
  FTM: { name: 'Fantom', icon: 'ftm' },
  GLMR: { name: 'Moonbeam', icon: 'glmr' },
  MOVR: { name: 'Moonriver', icon: 'movr' },
  KSM: { name: 'Kusama', icon: 'ksm' },
  EOS: { name: 'EOS', icon: 'eos' },
  ETC: { name: 'Ethereum Classic', shortName: 'ETH Classic', icon: 'etc' },
  LTC: { name: 'Litecoin', icon: 'ltc' },
  BCH: { name: 'Bitcoin Cash', shortName: 'BTC Cash', icon: 'bch' },
  DOGE: { name: 'Dogecoin', icon: 'doge' },
  BNB: { name: 'Binance Chain (BEP2)', shortName: 'Binance', icon: 'bnb' },
  ONE: { name: 'Harmony', icon: 'one' },
  KLAY: { name: 'Kaia', icon: 'kaia' },
  RON: { name: 'Ronin', icon: 'ron' },
  LRC: { name: 'Loopring', icon: 'lrc' },
  ICP: { name: 'Internet Computer', shortName: 'ICP', icon: 'icp' },
  EGLD: { name: 'MultiversX', icon: 'egld' },
  FLUX: { name: 'Flux', icon: 'flux' },
  KDA: { name: 'Kadena', icon: 'kda' },
  WAVES: { name: 'Waves', icon: 'waves' },
  XTZ: { name: 'Tezos', icon: 'xtz' },
  ACA: { name: 'Acala', icon: 'aca' },
  ALEO: { name: 'Aleo', icon: 'aleo' },
  ALPH: { name: 'Alephium', icon: 'alph' },
  APE: { name: 'ApeChain', icon: 'ape' },
  ASTR: { name: 'Astar', icon: 'astr' },
  AZERO: { name: 'Aleph Zero', icon: 'azero' },
  AZERO_EVM: { name: 'Aleph Zero EVM', shortName: 'Aleph Zero', icon: 'azero' },
  BAJU: { name: 'Bajun Network', shortName: 'Bajun', icon: 'baju' },
  BNCPOLKA: { name: 'Bifrost Polkadot', shortName: 'Bifrost', icon: 'bnc' },
  BNC: { name: 'Bifrost Kusama', shortName: 'Bifrost', icon: 'bnc' },
  BOBA: { name: 'Boba Network', shortName: 'Boba', icon: 'boba' },
  BSX: { name: 'Basilisk', icon: 'bsx' },
  CHZ: { name: 'Chiliz', icon: 'chz' },
  VIC: { name: 'Viction', icon: 'vic' },
  CCD: { name: 'Concordium', icon: 'ccd' },
  COREUM: { name: 'Coreum', icon: 'coreum' },
  DINGO: { name: 'Dingocoin', icon: 'dingo' },
  HDX: { name: 'Hydration', icon: 'hdx' },
  PLMC: { name: 'Polimec', icon: 'plmc' },
  MNT: { name: 'Mantle', icon: 'mnt' },
  ERG: { name: 'Ergo', icon: 'erg' },
  ECLIPSE: { name: 'Eclipse', icon: 'eclipse' },
  FUEL: { name: 'Fuel Ignition', shortName: 'Fuel', icon: 'fuel' },
  INK: { name: 'Ink', icon: 'ink' },
  SOPHON: { name: 'Sophon', icon: 'sophon' },
  UNICHAIN: { name: 'Unichain', icon: 'unichain' },
  WLD: { name: 'Worldchain', icon: 'wld' },
  ETHERLINK: { name: 'Etherlink', icon: 'etherlink' },
  MOB: { name: 'MobileCoin', icon: 'mob' },
  GALA: { name: 'Gala', icon: 'gala' },
  GUNZ: { name: 'Gunz', icon: 'gun' },
  HNT: { name: 'Helium', icon: 'hnt' },
  BITKUB: { name: 'BitKub', icon: 'kub' },
  IMMUTABLE_ZKEVM: { name: 'Immutable-zkEVM', shortName: 'Immutable', icon: 'imx' },
  INIT: { name: 'Initia', icon: 'init' },
  INTR: { name: 'Interlay', icon: 'intr' },
  KINESIS: { name: 'Kinesis', icon: 'kag' },
  KAR: { name: 'Karura', icon: 'kar' },
  KCC: { name: 'KuCoin Community Chain', shortName: 'KuCoin', icon: 'kcs' },
  KILT: { name: 'KILT Spiritnet', shortName: 'KILT', icon: 'kilt' },
  KINT: { name: 'Kintsugi', icon: 'kint' },
  KMD: { name: 'Komodo', icon: 'kmd' },
  LSK: { name: 'Lisk', icon: 'lsk' },
  METIS: { name: 'Metis Andromeda', shortName: 'Metis', icon: 'metis' },
  MYTH: { name: 'Myth', icon: 'myth' },
  OAS: { name: 'Oasys', icon: 'oas' },
  PEAQ_EVM: { name: 'Peaq (EVM)', shortName: 'Peaq', icon: 'peaq' },
  PEN: { name: 'Pendulum', icon: 'pen' },
  PI: { name: 'Pi', icon: 'pi' },
  POLYX: { name: 'Polymesh', icon: 'polyx' },
  Q: { name: 'QuantumCoin', icon: 'q' },
  QRL: { name: 'Quantum Resistant Ledger', shortName: 'QRL', icon: 'qrl' },
  QUBIC: { name: 'Qubic', icon: 'qubic' },
  RBX: { name: 'ReserveBlock', icon: 'rbx' },
  ROLLUX: { name: 'Rollux', icon: 'rollux' },
  THORCHAIN: { name: 'Thorchain', icon: 'rune' },
  S: { name: 'Sonic', icon: 's' },
  SONIC: { name: 'Sonic', icon: 's' },
  TERNOA: { name: 'Ternoa', icon: 'caps' },
  SX: { name: 'SX Network', icon: 'sol' },
  WAX: { name: 'WAX', icon: 'waxp' },
  XCH: { name: 'Chia Chain', icon: 'xch' },
  XPRT: { name: 'Persistence', icon: 'xprt' },
  ZETACHAIN: { name: 'ZetaChain', icon: 'zeta' },
  ZTG: { name: 'Zeitgeist', icon: 'ztg' },
  PLS: { name: 'Pulsechain', icon: 'pulsechain' },
  MEZO: { name: 'Mezo', icon: 'mezo' },
  BSV: { name: 'Bitcoin SV', shortName: 'BTC SV', icon: 'btc' },
  MONAD: { name: 'Monad', icon: 'mon' },
  SUPRA: { name: 'Supra', icon: 'supra' },
  SOMI: { name: 'Somnia', icon: 'somi' },
  VERIFIEDX: { name: 'VerifiedX', icon: 'vfx' },
  MOVE: { name: 'Movement', icon: 'move' },
  FLR: { name: 'Flare', icon: 'flr' },
  ASSETHUB: { name: 'Asset Hub Polkadot', shortName: 'Asset Hub', icon: 'dot' },
  BOB: { name: 'BOB', icon: 'btc' },
  GLUE: { name: 'Glue', icon: 'eth' },
  DYDX: { name: 'StarkEx', icon: 'stark' },
  OKC: { name: 'OKX Chain', icon: 'eth' },
  UNI_CHAIN: { name: 'UNI', icon: 'uni' },
  ZEC: { name: 'ZCash', icon: 'zec' },
};

// ── Full Banxa-supported cryptocurrencies ──
// Each entry: { code, name, chains[], popular }
// chains[] lists the blockchain codes this crypto is available on

export const CRYPTO_CURRENCIES: CryptoCurrency[] = [
  // --- Popular / Top coins ---
  { code: 'BTC', name: 'Bitcoin', chains: ['BTC', 'LN'], popular: true },
  { code: 'ETH', name: 'Ethereum', chains: ['ETH', 'ARB', 'BASE', 'BSC', 'OPTIMISM', 'LINEA', 'ZKSYNC', 'LRC', 'STARK', 'APE', 'ECLIPSE', 'FUEL', 'INK', 'SOPHON', 'UNICHAIN', 'WLD'], popular: true },
  { code: 'USDC', name: 'USD Coin', chains: ['ETH', 'SOL', 'ARB', 'BASE', 'BSC', 'MATIC', 'OPTIMISM', 'AVAX_C', 'ALGO', 'APT', 'CELO', 'FLOW', 'HBAR', 'LINEA', 'LRC', 'ZKSYNC', 'STARK', 'SUI', 'XLM', 'ECLIPSE', 'HDX', 'INK', 'LSK', 'METIS', 'MONAD', 'SEI_EVM', 'SONIC', 'SOPHON', 'SX', 'UNICHAIN', 'APE', 'BNCPOLKA', 'GLMR', 'PEAQ_EVM', 'PLMC', 'RON', 'UNI_CHAIN', 'WLD', 'BITKUB', 'BOB', 'DYDX', 'ETHERLINK', 'GLUE', 'ASSETHUB'], popular: true },
  { code: 'USDT', name: 'Tether', chains: ['ETH', 'BSC', 'TRON', 'ARB', 'MATIC', 'AVAX_C', 'SOL', 'OPTIMISM', 'ZKSYNC', 'STARK', 'APT', 'CELO', 'KLAY', 'LRC', 'LSK', 'ECLIPSE', 'ETHERLINK', 'OKC', 'PEAQ_EVM', 'BNCPOLKA', 'GLMR', 'HDX', 'PLMC', 'VIC', 'ASSETHUB'], popular: true },
  { code: 'SOL', name: 'Solana', chains: ['SOL'], popular: true },
  { code: 'XRP', name: 'Ripple', chains: ['XRP'], popular: true },
  { code: 'ADA', name: 'Cardano', chains: ['ADA'], popular: true },
  { code: 'DOGE', name: 'Dogecoin', chains: ['DOGE'], popular: true },
  { code: 'DOT', name: 'Polkadot', chains: ['DOT', 'BSC', 'BNCPOLKA', 'GLMR', 'HDX', 'PLMC'], popular: true },
  { code: 'AVAX', name: 'Avalanche', chains: ['AVAX_C', 'AVAX_X', 'BSC'], popular: true },
  { code: 'LINK', name: 'ChainLink', chains: ['ETH', 'BSC'], popular: true },
  { code: 'LTC', name: 'Litecoin', chains: ['LTC'], popular: true },

  // --- Major altcoins ---
  { code: 'AAVE', name: 'Aave', chains: ['ETH', 'BSC'], popular: false },
  { code: 'ACA', name: 'Acala', chains: ['ACA'], popular: false },
  { code: 'ALEO', name: 'Aleo', chains: ['ALEO'], popular: false },
  { code: 'ALGO', name: 'Algorand', chains: ['ALGO'], popular: false },
  { code: 'ALPH', name: 'Alephium', chains: ['ALPH'], popular: false },
  { code: 'APE', name: 'ApeCoin', chains: ['APE', 'ETH'], popular: false },
  { code: 'APT', name: 'Aptos', chains: ['APT'], popular: false },
  { code: 'ARB', name: 'Arbitrum', chains: ['ARB'], popular: false },
  { code: 'ASTR', name: 'Astar', chains: ['ASTR'], popular: false },
  { code: 'ATOM', name: 'Cosmos', chains: ['ATOM'], popular: false },
  { code: 'AUDD', name: 'Novatti Australian Digital Dollar', chains: ['ETH'], popular: false },
  { code: 'AVT', name: 'Aventus', chains: ['ETH'], popular: false },
  { code: 'AXS', name: 'Axie Infinity', chains: ['ETH', 'RON'], popular: false },
  { code: 'AZERO', name: 'Aleph Zero', chains: ['AZERO', 'AZERO_EVM'], popular: false },
  { code: 'BAJU', name: 'Bajun Network', chains: ['BAJU'], popular: false },
  { code: 'BARSIK', name: "Hasbulla's Cat", chains: ['SOL'], popular: false },
  { code: 'BAT', name: 'Basic Attention Token', chains: ['ETH', 'BSC'], popular: false },
  { code: 'BCH', name: 'Bitcoin Cash', chains: ['BCH'], popular: false },
  { code: 'BNB', name: 'Binance Coin', chains: ['BNB', 'BSC'], popular: false },
  { code: 'BNC', name: 'Bifrost Native Coin', chains: ['BNC', 'BNCPOLKA'], popular: false },
  { code: 'BOBA', name: 'Boba Network', chains: ['BOBA', 'ETH'], popular: false },
  { code: 'BORA', name: 'Bora', chains: ['KLAY'], popular: false },
  { code: 'BRETT', name: 'Brett', chains: ['BASE'], popular: false },
  { code: 'BSX', name: 'Basilisk', chains: ['BSX'], popular: false },
  { code: 'BZR', name: 'Bazaars', chains: ['ETH'], popular: false },
  { code: 'CAGA', name: 'Crypto Asset Governance Alliance', chains: ['ETH'], popular: false },
  { code: 'CAKE', name: 'PancakeSwap', chains: ['BSC'], popular: false },
  { code: 'CAPS', name: 'Caps', chains: ['TERNOA'], popular: false },
  { code: 'CELO', name: 'Celo', chains: ['CELO'], popular: false },
  { code: 'CHZ', name: 'Chiliz', chains: ['CHZ', 'BNB', 'ETH'], popular: false },
  { code: 'C98', name: 'Coin98', chains: ['VIC'], popular: false },
  { code: 'CCD', name: 'Concordium', chains: ['CCD'], popular: false },
  { code: 'COMP', name: 'Compound', chains: ['ETH', 'BSC'], popular: false },
  { code: 'COREUM', name: 'Coreum', chains: ['COREUM', 'XRP'], popular: false },
  { code: 'CRO', name: 'Cronos', chains: ['CRO'], popular: false },
  { code: 'CUSD', name: 'Celo Dollar', chains: ['CELO'], popular: false },
  { code: 'CVC', name: 'Civic', chains: ['ETH'], popular: false },
  { code: 'DAI', name: 'Dai', chains: ['ETH', 'BASE', 'MATIC'], popular: false },
  { code: 'DINGO', name: 'Dingocoin', chains: ['DINGO'], popular: false },
  { code: 'EGLD', name: 'MultiversX', chains: ['EGLD'], popular: false },
  { code: 'EMBER', name: 'Ember Sword', chains: ['MNT'], popular: false },
  { code: 'EOS', name: 'EOS', chains: ['EOS'], popular: false },
  { code: 'ERG', name: 'Ergo', chains: ['ERG'], popular: false },
  { code: 'ETC', name: 'Ethereum Classic', chains: ['ETC', 'BSC'], popular: false },
  { code: 'EUR', name: 'EURC', chains: ['BASE'], popular: false },
  { code: 'EUROC', name: 'Euro Coin', chains: ['ETH'], popular: false },
  { code: 'EURQ', name: 'Quantoz EURQ', chains: ['ETH'], popular: false },
  { code: 'EURR', name: 'StablR EUR', chains: ['ETH'], popular: false },
  { code: 'EUSD', name: 'Electronic USD', chains: ['MOB'], popular: false },
  { code: 'EXIT', name: 'EXIT - Designer Token', chains: ['BSC'], popular: false },
  { code: 'FDUSD', name: 'First Digital USD', chains: ['SUI'], popular: false },
  { code: 'FIL', name: 'Filecoin', chains: ['FIL', 'BSC'], popular: false },
  { code: 'FLOW', name: 'Flow', chains: ['FLOW'], popular: false },
  { code: 'FLR', name: 'Flare', chains: ['FLR'], popular: false },
  { code: 'FLUX', name: 'Flux', chains: ['FLUX'], popular: false },
  { code: 'FTM', name: 'Fantom', chains: ['FTM', 'ETH', 'BSC'], popular: false },
  { code: 'GALA', name: 'Gala', chains: ['GALA'], popular: false },
  { code: 'GLMR', name: 'Moonbeam', chains: ['GLMR'], popular: false },
  { code: 'GMX', name: 'GMX', chains: ['ARB'], popular: false },
  { code: 'GNUS', name: 'Genius AI', chains: ['ETH'], popular: false },
  { code: 'GRT', name: 'The Graph', chains: ['ETH', 'ARB'], popular: false },
  { code: 'GUN', name: 'Gunz', chains: ['GUNZ'], popular: false },
  { code: 'HBAR', name: 'Hedera', chains: ['HBAR'], popular: false },
  { code: 'HDX', name: 'HydraDX', chains: ['HDX'], popular: false },
  { code: 'HNT', name: 'Helium', chains: ['HNT'], popular: false },
  { code: 'HTP', name: 'HowToPay', chains: ['BSC'], popular: false },
  { code: 'HVLO', name: 'Hivello', chains: ['SOL'], popular: false },
  { code: 'ICP', name: 'Internet Computer', chains: ['ICP'], popular: false },
  { code: 'IMX', name: 'Immutable X', chains: ['ETH'], popular: false },
  { code: 'INIT', name: 'Initia', chains: ['INIT'], popular: false },
  { code: 'INJ', name: 'Injective', chains: ['INJ'], popular: false },
  { code: 'INTR', name: 'Interlay', chains: ['INTR'], popular: false },
  { code: 'JAM', name: 'Jam Tune.FM', chains: ['HBAR'], popular: false },
  { code: 'KAG', name: 'Silver', chains: ['KINESIS'], popular: false },
  { code: 'KAIA', name: 'Kaia', chains: ['KLAY'], popular: false },
  { code: 'KAR', name: 'Karura', chains: ['KAR'], popular: false },
  { code: 'KAU', name: 'Gold', chains: ['KINESIS'], popular: false },
  { code: 'KCS', name: 'KuCoin Token', chains: ['ETH', 'KCC'], popular: false },
  { code: 'KDA', name: 'Kadena', chains: ['KDA'], popular: false },
  { code: 'KILT', name: 'KILT', chains: ['KILT'], popular: false },
  { code: 'KINT', name: 'Kintsugi', chains: ['KINT'], popular: false },
  { code: 'KLV', name: 'Klever', chains: ['TRON'], popular: false },
  { code: 'KMD', name: 'Komodo', chains: ['KMD'], popular: false },
  { code: 'KSM', name: 'Kusama', chains: ['KSM'], popular: false },
  { code: 'KUB', name: 'Bitkub Coin', chains: ['BITKUB'], popular: false },
  { code: 'LOOKS', name: 'LooksRare', chains: ['ETH'], popular: false },
  { code: 'LRC', name: 'Loopring', chains: ['ETH', 'LRC'], popular: false },
  { code: 'LSK', name: 'Lisk', chains: ['LSK'], popular: false },
  { code: 'MANA', name: 'Decentraland', chains: ['ETH'], popular: false },
  { code: 'MBX', name: 'Marblex', chains: ['IMMUTABLE_ZKEVM', 'KLAY'], popular: false },
  { code: 'MELANIA', name: 'Melania', chains: ['SOL'], popular: false },
  { code: 'METAMASKUSD', name: 'Metamask USD', chains: ['ETH', 'LINEA'], popular: false },
  { code: 'METIS', name: 'Metis', chains: ['METIS'], popular: false },
  { code: 'MEZOUSD', name: 'MUSD', chains: ['ETH', 'MEZO'], popular: false },
  { code: 'MKR', name: 'Maker', chains: ['ETH', 'BSC'], popular: false },
  { code: 'MNEE', name: 'MNEE', chains: ['BSV', 'ETH'], popular: false },
  { code: 'MNT', name: 'Mantle', chains: ['ETH', 'MNT'], popular: false },
  { code: 'MOB', name: 'MobileCoin', chains: ['MOB'], popular: false },
  { code: 'MON', name: 'Monad', chains: ['MONAD'], popular: false },
  { code: 'MOVE', name: 'Movement', chains: ['MOVE'], popular: false },
  { code: 'MOVR', name: 'Moonriver', chains: ['MOVR'], popular: false },
  { code: 'MYTH', name: 'Myth', chains: ['MYTH'], popular: false },
  { code: 'NEAR', name: 'NEAR Protocol', chains: ['NEAR', 'BSC'], popular: false },
  { code: 'NEIRO', name: 'NEIRO Ethereum', chains: ['ETH'], popular: false },
  { code: 'OAS', name: 'Oasys', chains: ['OAS'], popular: false },
  { code: 'OMG', name: 'OmiseGo', chains: ['ETH'], popular: false },
  { code: 'OMUSD', name: 'Open Money USD', chains: ['ETH'], popular: false },
  { code: 'ONE', name: 'Harmony', chains: ['ONE', 'BNB', 'ETH'], popular: false },
  { code: 'OP', name: 'Optimism', chains: ['OPTIMISM'], popular: false },
  { code: 'ORDER', name: 'Orderly Network', chains: ['ETH'], popular: false },
  { code: 'PEAQ', name: 'Peaq', chains: ['PEAQ_EVM'], popular: false },
  { code: 'PEN', name: 'Pendulum', chains: ['PEN'], popular: false },
  { code: 'PERC', name: 'Perion', chains: ['ETH'], popular: false },
  { code: 'PI', name: 'Pi', chains: ['PI'], popular: false },
  { code: 'PLMC', name: 'Polimec', chains: ['PLMC'], popular: false },
  { code: 'POL', name: 'Polygon', chains: ['ETH', 'MATIC'], popular: false },
  { code: 'POLYX', name: 'Polymesh', chains: ['POLYX'], popular: false },
  { code: 'PROPC', name: 'Propchain', chains: ['ETH'], popular: false },
  { code: 'PSTAKE', name: 'pSTAKE Finance', chains: ['ETH'], popular: false },
  { code: 'Q', name: 'QuantumCoin', chains: ['Q'], popular: false },
  { code: 'QRDO', name: 'Qredo', chains: ['ETH'], popular: false },
  { code: 'QRL', name: 'Quantum Resistant Ledger', chains: ['QRL'], popular: false },
  { code: 'QUBIC', name: 'QUBIC', chains: ['QUBIC'], popular: false },
  { code: 'RBX', name: 'ReserveBlock', chains: ['RBX'], popular: false },
  { code: 'REVU', name: 'Revuto', chains: ['ADA'], popular: false },
  { code: 'RITE', name: 'Ritestream', chains: ['BSC'], popular: false },
  { code: 'RLUSD', name: 'Ripple USD', chains: ['ETH'], popular: false },
  { code: 'RON', name: 'Ronin', chains: ['RON'], popular: false },
  { code: 'RUNE', name: 'THORChain', chains: ['THORCHAIN'], popular: false },
  { code: 'S', name: 'Sonic', chains: ['S'], popular: false },
  { code: 'SAND', name: 'The Sandbox', chains: ['ETH', 'BSC'], popular: false },
  { code: 'SEI', name: 'SEI', chains: ['SEI_EVM'], popular: false },
  { code: 'SNX', name: 'Synthetix', chains: ['ETH'], popular: false },
  { code: 'SOIL', name: 'Sun Minimeal', chains: ['PLS'], popular: false },
  { code: 'SOLO', name: 'Sologenic', chains: ['XRP'], popular: false },
  { code: 'SOMI', name: 'Somnia', chains: ['SOMI'], popular: false },
  { code: 'STRK', name: 'Starknet', chains: ['STARK'], popular: false },
  { code: 'SUI', name: 'Sui', chains: ['SUI'], popular: false },
  { code: 'SUPR', name: 'SuperDapp', chains: ['ROLLUX'], popular: false },
  { code: 'SUPRA', name: 'Supra', chains: ['SUPRA'], popular: false },
  { code: 'SUSHI', name: 'SushiSwap', chains: ['ETH', 'BSC'], popular: false },
  { code: 'SYS', name: 'Syscoin', chains: ['ROLLUX'], popular: false },
  { code: 'TBTC', name: 'tBTC', chains: ['SUI', 'STARK'], popular: false },
  { code: 'TON', name: 'Toncoin', chains: ['TON'], popular: false },
  { code: 'TRUMP', name: 'TRUMP', chains: ['SOL'], popular: false },
  { code: 'TRX', name: 'Tron', chains: ['TRON', 'BSC'], popular: false },
  { code: 'TWT', name: 'Trust Wallet Token', chains: ['BSC'], popular: false },
  { code: 'UNI', name: 'Uniswap', chains: ['ETH', 'BSC'], popular: false },
  { code: 'URANIUM', name: 'Uranium', chains: ['ETHERLINK'], popular: false },
  { code: 'USD0', name: 'Usual USD', chains: ['ETH'], popular: false },
  { code: 'USD1', name: 'World Liberty Financial USD', chains: ['SOL'], popular: false },
  { code: 'USDE', name: 'Ethena USDe', chains: ['ETH'], popular: false },
  { code: 'USDP', name: 'Pax Dollar', chains: ['ETH'], popular: false },
  { code: 'USDQ', name: 'Quantoz USDQ', chains: ['ETH'], popular: false },
  { code: 'USDR', name: 'StablR USD', chains: ['ETH'], popular: false },
  { code: 'USDT0', name: 'USDT0', chains: ['INK'], popular: false },
  { code: 'VFX', name: 'VerifiedX', chains: ['VERIFIEDX'], popular: false },
  { code: 'VERSE', name: 'Verse', chains: ['ETH', 'MATIC'], popular: false },
  { code: 'VIC', name: 'Viction', chains: ['VIC'], popular: false },
  { code: 'WAL', name: 'Walrus', chains: ['SUI'], popular: false },
  { code: 'WAVES', name: 'Waves', chains: ['WAVES'], popular: false },
  { code: 'WAXP', name: 'WAX', chains: ['WAX'], popular: false },
  { code: 'WBTC', name: 'Wrapped Bitcoin', chains: ['ETH', 'STARK'], popular: false },
  { code: 'WETH', name: 'Wrapped Ether', chains: ['ETHERLINK'], popular: false },
  { code: 'WLD', name: 'Worldcoin', chains: ['WLD'], popular: false },
  { code: 'WLFI', name: 'World Liberty Financial', chains: ['ETH'], popular: false },
  { code: 'WOLF', name: 'LandWolf', chains: ['ETH'], popular: false },
  { code: 'WOMBAT', name: 'Wombat', chains: ['MATIC'], popular: false },
  { code: 'XCH', name: 'Chia', chains: ['XCH'], popular: false },
  { code: 'XLM', name: 'Stellar Lumens', chains: ['XLM', 'BSC'], popular: false },
  { code: 'XPRT', name: 'Persistence', chains: ['XPRT'], popular: false },
  { code: 'XTZ', name: 'Tezos', chains: ['XTZ', 'ETHERLINK'], popular: false },
  { code: 'YFI', name: 'yearn.finance', chains: ['ETH'], popular: false },
  { code: 'ZBU', name: 'Zeebu', chains: ['BSC'], popular: false },
  { code: 'ZEC', name: 'ZCash', chains: ['ZEC'], popular: false },
  { code: 'ZCHF', name: 'Frankencoin', chains: ['ETH'], popular: false },
  { code: 'ZETA', name: 'Zeta', chains: ['ETH', 'ZETACHAIN'], popular: false },
  { code: 'ZTG', name: 'Zeitgeist', chains: ['ZTG'], popular: false },
];

// ── Crypto code -> name lookup (for API data that only has codes) ──
export const CRYPTO_NAMES: Record<string, string> = Object.fromEntries(
  CRYPTO_CURRENCIES.map(c => [c.code, c.name])
);

// ── Fiat currency code -> ISO country code for flag lookup ──

export const FIAT_TO_COUNTRY: Record<string, string> = {
  AED: 'ae',
  ALL: 'al',
  ARS: 'ar',
  AUD: 'au',
  BRL: 'br',
  CAD: 'ca',
  CHF: 'ch',
  CLP: 'cl',
  CZK: 'cz',
  DKK: 'dk',
  EUR: 'eu',
  GBP: 'gb',
  HKD: 'hk',
  IDR: 'id',
  INR: 'in',
  JPY: 'jp',
  KRW: 'kr',
  MXN: 'mx',
  NGN: 'ng',
  NOK: 'no',
  NZD: 'nz',
  PHP: 'ph',
  PLN: 'pl',
  QAR: 'qa',
  SAR: 'sa',
  SEK: 'se',
  SGD: 'sg',
  THB: 'th',
  TRY: 'tr',
  TWD: 'tw',
  USD: 'us',
  VND: 'vn',
  ZAR: 'za',
};

// ── Icon URL helpers ──

/**
 * Get the icon URL for a crypto ticker.
 * Icons are stored as SVGs in /crypto-icons/{ticker}.svg
 * @param baseUrl Optional base URL prefix (e.g. CDN URL). Defaults to root-relative.
 */
export function getCryptoIconUrl(ticker: string, baseUrl = ''): string {
  return `${baseUrl}/crypto-icons/${ticker.toLowerCase()}.svg`;
}

/**
 * Get the flag URL for a fiat currency code.
 * Flags are stored as SVGs in /flags/{country_code}.svg
 * @param baseUrl Optional base URL prefix (e.g. CDN URL). Defaults to root-relative.
 */
export function getFiatFlagUrl(currencyCode: string, baseUrl = ''): string {
  const country = FIAT_TO_COUNTRY[currencyCode] || 'us';
  return `${baseUrl}/flags/${country}.svg`;
}

/**
 * Get the icon URL for a blockchain/chain.
 * Uses the chain's native token icon.
 * @param baseUrl Optional base URL prefix (e.g. CDN URL). Defaults to root-relative.
 */
/** Lookup a chain in BLOCKCHAINS, handling canonical ids, legacy codes, and separator differences */
export function getChainInfo(chainCode: string): BlockchainInfo | undefined {
  return BLOCKCHAINS[chainCode]
    || BLOCKCHAINS[chainCode.toLowerCase()]
    || BLOCKCHAINS[chainCode.toUpperCase()]
    || BLOCKCHAINS[chainCode.toUpperCase().replace(/-/g, '_')]
    || BLOCKCHAINS[chainCode.toLowerCase().replace(/-/g, '_')];
}

/**
 * Get the icon URL for a provider/gateway.
 * Icons are stored as SVGs in /provider-icons/{id}.svg
 * @param baseUrl Optional base URL prefix (e.g. CDN URL). Defaults to root-relative.
 */
export function getProviderIconUrl(gatewayId: string, baseUrl = ''): string {
  return `${baseUrl}/provider-icons/${gatewayId.toLowerCase()}.svg`;
}

export function getChainIconUrl(chainCode: string, baseUrl = ''): string {
  const chain = getChainInfo(chainCode);
  if (chain) return `${baseUrl}/crypto-icons/${chain.icon}.svg`;
  return `${baseUrl}/crypto-icons/${chainCode.toLowerCase()}.svg`;
}

// ── Fiat currencies ──

export const FIAT_CURRENCIES: FiatCurrency[] = [
  { code: 'ALL', name: 'Albania Lek' },
  { code: 'ARS', name: 'Argentina Peso' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'INR', name: 'Indian Rupee' },
];

// ── Skin presets ──

export const SKINS: Skin[] = [
  { id: 'blue',   name: 'Blue',   accent: '#0077FF' },
  { id: 'violet', name: 'Violet', accent: '#7C3AED' },
  { id: 'green',  name: 'Green',  accent: '#10B981' },
  { id: 'orange', name: 'Orange', accent: '#F59E0B' },
  { id: 'rose',   name: 'Rose',   accent: '#F43F5E' },
  { id: 'mono',   name: 'Mono',   accent: null      },
];

// ── Mock rates ──

export const MOCK_RATES: Record<string, Record<string, number>> = {
  USDC: { USD: 0.9962, EUR: 1.0814, GBP: 1.2573, JPY: 0.00667, CAD: 0.7198, AUD: 0.6384, BRL: 0.1726, CHF: 1.1306, MXN: 0.0487, INR: 0.01187, ARS: 0.000855, ALL: 0.01058 },
  USDT: { USD: 0.9958, EUR: 1.0810, GBP: 1.2568, JPY: 0.00666, CAD: 0.7194, AUD: 0.6380, BRL: 0.1724, CHF: 1.1301, MXN: 0.0486, INR: 0.01186, ARS: 0.000854, ALL: 0.01057 },
  BTC:  { USD: 0.0000104, EUR: 0.00001129, GBP: 0.00001313, JPY: 0.0000000696, CAD: 0.00000751, AUD: 0.00000666, BRL: 0.0000018, CHF: 0.0000118, MXN: 0.000000508, INR: 0.000000124, ARS: 0.00000000892, ALL: 0.00000011 },
  ETH:  { USD: 0.000316, EUR: 0.000343, GBP: 0.000399, JPY: 0.00000211, CAD: 0.000228, AUD: 0.000202, BRL: 0.0000547, CHF: 0.000358, MXN: 0.0000154, INR: 0.00000376, ARS: 0.000000271, ALL: 0.00000335 },
  SOL:  { USD: 0.00424, EUR: 0.004607, GBP: 0.005356, JPY: 0.0000284, CAD: 0.003063, AUD: 0.002716, BRL: 0.000734, CHF: 0.004812, MXN: 0.000207, INR: 0.0000505, ARS: 0.00000364, ALL: 0.000045 },
  XRP:  { USD: 0.4081, EUR: 0.4432, GBP: 0.5153, JPY: 0.00273, CAD: 0.2948, AUD: 0.2614, BRL: 0.0707, CHF: 0.4630, MXN: 0.01992, INR: 0.00486, ARS: 0.000350, ALL: 0.00433 },
  DOGE: { USD: 3.896, EUR: 4.232, GBP: 4.920, JPY: 0.02607, CAD: 2.815, AUD: 2.496, BRL: 0.6750, CHF: 4.421, MXN: 0.1902, INR: 0.04641, ARS: 0.003345, ALL: 0.04134 },
  ADA:  { USD: 1.333, EUR: 1.448, GBP: 1.683, JPY: 0.00892, CAD: 0.963, AUD: 0.854, BRL: 0.2309, CHF: 1.512, MXN: 0.0651, INR: 0.01588, ARS: 0.001145, ALL: 0.01414 },
};

// ── Stablecoin detection ──

const STABLECOINS = new Set([
  'USDC', 'USDT', 'DAI', 'BUSD', 'TUSD', 'FDUSD', 'PYUSD',
  'USDP', 'USDE', 'USDQ', 'USDR', 'USDT0', 'RLUSD',
  'CUSD', 'EUSD', 'OMUSD', 'MEZOUSD', 'METAMASKUSD',
  'USD0', 'USD1', 'AUDD', 'EUROC', 'EURQ', 'EURR', 'ZCHF',
]);

/** Check if a crypto code is a known stablecoin */
export function isStablecoin(code: string): boolean {
  return STABLECOINS.has(code.toUpperCase());
}

// ── Helper functions ──

export function getMockRate(crypto: string, fiat: string): number {
  return MOCK_RATES[crypto]?.[fiat] ?? MOCK_RATES['USDC']?.[fiat] ?? 1;
}

export function parseAmount(str: string): number {
  return parseFloat(str.replace(/,/g, '')) || 0;
}

export function formatOutput(num: number): string {
  if (num === 0) return '0';
  if (num >= 1000) return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (num >= 1) return num.toFixed(2);
  if (num >= 0.01) return num.toFixed(4);
  const s = num.toPrecision(4);
  return parseFloat(s).toString();
}

export const isValidHex = (str: string): boolean => /^#([0-9A-Fa-f]{3}){1,2}$/.test(str);

// ── Validation constants ──

export const MIN_FIAT_AMOUNT = 10;
export const MIN_CRYPTO_AMOUNT = 0.0001;

// ── Pre-built flat list of every crypto+chain combination ──

export const CRYPTO_CHAIN_COMBOS: CryptoChainCombo[] = (() => {
  const list: CryptoChainCombo[] = [];
  for (const c of CRYPTO_CURRENCIES) {
    for (const chain of c.chains) {
      list.push({ code: c.code, name: c.name, chain, popular: c.popular });
    }
  }
  return list;
})();
